import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Alert, Chip, Box, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface AlertItem {
  id: number;
  alert_id: string;
  cad_number: string | null;
  call_type: string | null;
  call_type_code: string | null;
  priority: number | null;
  address: string | null;
  city: string | null;
  cross_streets: string | null;
  narrative: string | null;
  units_dispatched: string | null;
  zone: string | null;
  source_agent_id: string | null;
  status: string;
  timestamp: string | null;
}

interface Delivery {
  id: number;
  agent_pk: number;
  status: string;
  tone_played: string | null;
  tts_text: string | null;
  details: string | null;
  delivered_at: string | null;
  acked_at: string | null;
}

const statusColor = (s: string) => {
  switch (s) {
    case 'delivered': return 'success';
    case 'failed': return 'error';
    case 'cancelled': return 'default';
    case 'pending': return 'warning';
    default: return 'info';
  }
};

export default function AlertsPage() {
  const { tenantId } = useAuth();
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<AlertItem | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/alerts?limit=200`);
      setAlerts(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (a: AlertItem) => {
    if (!tenantId) return;
    setSelectedAlert(a);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/alerts/${a.alert_id}`);
      setDeliveries(res.data.deliveries);
    } catch {
      setDeliveries([]);
    }
    setDetailOpen(true);
  };

  const handleCancel = async (a: AlertItem) => {
    if (!tenantId || !window.confirm('Cancel this alert?')) return;
    try {
      await api.post(`/api/tenants/${tenantId}/alerts/${a.alert_id}/cancel`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Cancel failed');
    }
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Alert History</Typography>
        <Button onClick={load} variant="outlined">Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>CAD #</TableCell>
              <TableCell>Call Type</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Units</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center">Loading...</TableCell></TableRow>
            ) : alerts.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No alerts</TableCell></TableRow>
            ) : alerts.map(a => (
              <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(a)}>
                <TableCell>{formatTime(a.timestamp)}</TableCell>
                <TableCell>{a.cad_number || '—'}</TableCell>
                <TableCell>{a.call_type || '—'}</TableCell>
                <TableCell>{a.address}{a.city ? `, ${a.city}` : ''}</TableCell>
                <TableCell>{a.units_dispatched || '—'}</TableCell>
                <TableCell><Chip label={a.status} size="small" color={statusColor(a.status) as any} /></TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  {a.status !== 'cancelled' && (
                    <Button size="small" color="warning" startIcon={<Cancel />} onClick={() => handleCancel(a)}>
                      Cancel
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Alert Detail — {selectedAlert?.cad_number || selectedAlert?.alert_id}</DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Alert ID:</strong> {selectedAlert.alert_id}</Typography>
                <Typography variant="body2"><strong>CAD #:</strong> {selectedAlert.cad_number || '—'}</Typography>
                <Typography variant="body2"><strong>Call Type:</strong> {selectedAlert.call_type} ({selectedAlert.call_type_code})</Typography>
                <Typography variant="body2"><strong>Priority:</strong> {selectedAlert.priority ?? '—'}</Typography>
                <Typography variant="body2"><strong>Address:</strong> {selectedAlert.address}, {selectedAlert.city}</Typography>
                <Typography variant="body2"><strong>Zone:</strong> {selectedAlert.zone || '—'}</Typography>
                <Typography variant="body2"><strong>Units:</strong> {selectedAlert.units_dispatched || '—'}</Typography>
                <Typography variant="body2"><strong>Source Agent:</strong> {selectedAlert.source_agent_id || '—'}</Typography>
              </Box>
              {selectedAlert.narrative && (
                <Typography variant="body2" sx={{ mb: 2 }}><strong>Narrative:</strong> {selectedAlert.narrative}</Typography>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Delivery Log</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tone</TableCell>
                    <TableCell>Delivered At</TableCell>
                    <TableCell>Acked At</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveries.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center">No deliveries</TableCell></TableRow>
                  ) : deliveries.map(d => (
                    <TableRow key={d.id}>
                      <TableCell>{d.agent_pk}</TableCell>
                      <TableCell><Chip label={d.status} size="small" color={statusColor(d.status) as any} /></TableCell>
                      <TableCell>{d.tone_played || '—'}</TableCell>
                      <TableCell>{formatTime(d.delivered_at)}</TableCell>
                      <TableCell>{formatTime(d.acked_at)}</TableCell>
                      <TableCell>{d.details || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
